import { useState, useEffect } from 'react';
import { api, getErrorMessage } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { 
  LogOut, Hotel, UtensilsCrossed, Sparkles, Plus, Trash2, 
  Receipt, Percent, CheckCircle2, AlertTriangle, Loader2, Lock
} from 'lucide-react';
import { toast } from 'sonner';
import { useTabs } from '@/context/TabsContext';
import MultiPaymentInput from '@/components/MultiPaymentInput';
import { useResourceLock } from '@/hooks/useResourceLock';

export default function CheckoutTab({ tabId, data }) {
  const { closeTab } = useTabs();
  const { booking, onComplete } = data || {};
  
  // Acquire lock on mount
  const { hasLock, lockedBy } = useResourceLock('booking', booking?.id, 'checkout', true);
  
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [roomCharges, setRoomCharges] = useState(0);
  const [restaurantOrders, setRestaurantOrders] = useState([]);
  const [serviceOrders, setServiceOrders] = useState([]);
  const [additionalCharges, setAdditionalCharges] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState('amount');
  const [discountReason, setDiscountReason] = useState('');
  const [payments, setPayments] = useState([]);
  const [notes, setNotes] = useState('');
  const [guest, setGuest] = useState(null);
  const [room, setRoom] = useState(null);
  const [calculatedNights, setCalculatedNights] = useState(1);

  const [newCharge, setNewCharge] = useState({ description: '', amount: '' });
  const [manualRestaurantCharges, setManualRestaurantCharges] = useState([]);
  const [manualServiceCharges, setManualServiceCharges] = useState([]);
  
  const [taxSettings, setTaxSettings] = useState({
    room_tax: 12,
    room_service_food_tax: 5,
    room_service_tax: 18,
    restaurant_food_tax: 5,
    restaurant_beverages_tax: 18,
    other_services_tax: 18
  });

  useEffect(() => {
    if (booking) {
      loadCheckoutData();
    }
  }, [booking]);

  const loadCheckoutData = async () => {
    setLoading(true);
    try {
      const [guestRes, roomRes, ordersRes, servicesRes] = await Promise.all([
        api.get(`/guests/${booking.guest_id}`).catch(() => ({ data: null })),
        api.get(`/rooms/${booking.room_id}`).catch(() => ({ data: null })),
        api.get(`/orders?booking_id=${booking.id}`).catch(() => ({ data: [] })),
        api.get(`/service-orders?booking_id=${booking.id}`).catch(() => ({ data: [] }))
      ]);

      setGuest(guestRes.data);
      setRoom(roomRes.data);
      setRestaurantOrders(ordersRes.data || []);
      setServiceOrders(servicesRes.data || []);
      
      let nights = booking.total_nights;
      if (!nights) {
        const checkIn = new Date(booking.check_in || booking.check_in_date);
        const checkOut = new Date(booking.check_out || booking.check_out_date);
        nights = Math.round((checkOut - checkIn) / (1000 * 60 * 60 * 24));
        if (isNaN(nights) || nights < 1) nights = 1;
      }
      setCalculatedNights(nights);
      const roomRate = booking.room_rate || (booking.total_amount / 1.12 / nights);
      setRoomCharges(roomRate * nights);
      setDiscount(booking.discount || 0);
      
      try {
        const taxRes = await api.get('/saas/hotels/tax-settings');
        if (taxRes.data?.tax_settings) {
          setTaxSettings(taxRes.data.tax_settings);
        }
      } catch (e) {
        console.log('Using default tax settings');
      }
      
    } catch (error) {
      console.error('Error loading checkout data:', error);
      toast.error('Failed to load checkout data');
    } finally {
      setLoading(false);
    }
  };

  const addCharge = () => {
    if (!newCharge.description.trim() || !newCharge.amount) {
      toast.error('Please enter description and amount');
      return;
    }
    setAdditionalCharges([
      ...additionalCharges,
      { id: Date.now(), description: newCharge.description.trim(), amount: parseFloat(newCharge.amount) }
    ]);
    setNewCharge({ description: '', amount: '' });
  };

  const removeCharge = (chargeId) => {
    setAdditionalCharges(additionalCharges.filter(c => c.id !== chargeId));
  };

  // Calculate totals
  const restaurantTotal = restaurantOrders.reduce((sum, o) => sum + (o.total_amount || o.total || 0), 0) +
    manualRestaurantCharges.reduce((sum, c) => sum + c.amount, 0);
  const serviceTotal = serviceOrders.reduce((sum, o) => sum + (o.total_amount || 0), 0) +
    manualServiceCharges.reduce((sum, c) => sum + c.amount, 0);
  const additionalTotal = additionalCharges.reduce((sum, c) => sum + c.amount, 0);
  const subtotal = roomCharges + restaurantTotal + serviceTotal + additionalTotal;
  
  const discountAmount = discountType === 'percent' ? (subtotal * discount / 100) : discount;
  const discountRatio = subtotal > 0 ? (subtotal - discountAmount) / subtotal : 1;
  
  const roomTaxRate = taxSettings.room_tax / 100;
  const restaurantTaxRate = taxSettings.restaurant_food_tax / 100;
  const serviceTaxRate = taxSettings.other_services_tax / 100;
  const additionalTaxRate = taxSettings.other_services_tax / 100;
  
  const roomTax = roomCharges * discountRatio * roomTaxRate;
  const restaurantTax = restaurantTotal * discountRatio * restaurantTaxRate;
  const serviceTax = serviceTotal * discountRatio * serviceTaxRate;
  const additionalTax = additionalTotal * discountRatio * additionalTaxRate;
  
  const totalTax = roomTax + restaurantTax + serviceTax + additionalTax;
  const taxableAmount = subtotal - discountAmount;
  const grandTotal = taxableAmount + totalTax;
  const amountPaid = booking?.advance_paid || 0;
  const balanceDue = grandTotal - amountPaid;
  
  const totalPayments = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  const isPaymentBalanced = Math.abs(balanceDue - totalPayments) < 0.01;

  const handleCheckout = async () => {
    if (balanceDue > 0 && payments.length === 0) {
      toast.error('Please add at least one payment method');
      return;
    }
    
    if (balanceDue > 0 && !isPaymentBalanced) {
      toast.error('Payment amounts must equal the balance due');
      return;
    }

    setProcessing(true);
    try {
      const paymentTransactions = payments.map(p => ({
        method: p.method,
        amount: parseFloat(p.amount) || 0,
        transaction_id: p.transaction_id || null,
        card_last_four: p.card_last_four || null,
        card_type: p.card_type || null,
        upi_id: p.upi_id || null,
        bank_name: p.bank_name || null,
        bank_reference: p.bank_reference || null
      }));

      const invoiceData = {
        booking_id: booking.id,
        guest_id: booking.guest_id,
        room_id: booking.room_id,
        invoice_type: 'checkout',
        room_charges: roomCharges,
        restaurant_charges: restaurantTotal,
        service_charges: serviceTotal,
        additional_charges: additionalCharges,
        manual_restaurant_charges: manualRestaurantCharges,
        manual_service_charges: manualServiceCharges,
        subtotal: subtotal,
        discount: discountAmount,
        discount_reason: discountReason,
        tax_breakdown: {
          room_tax: { amount: roomTax, rate: taxSettings.room_tax },
          restaurant_tax: { amount: restaurantTax, rate: taxSettings.restaurant_food_tax },
          service_tax: { amount: serviceTax, rate: taxSettings.other_services_tax },
          additional_tax: { amount: additionalTax, rate: taxSettings.other_services_tax }
        },
        tax: totalTax,
        total: grandTotal,
        amount_paid: amountPaid + totalPayments,
        balance_due: Math.max(0, balanceDue - totalPayments),
        payment_transactions: paymentTransactions,
        notes: notes,
        restaurant_orders: restaurantOrders.map(o => o.id),
        service_orders: serviceOrders.map(o => o.id)
      };

      await api.post('/invoices/checkout', invoiceData);
      await api.patch(`/bookings/${booking.id}/checkout`);
      
      toast.success('Checkout completed successfully!');
      onComplete?.();
      closeTab(tabId);
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(getErrorMessage(error, 'Failed to complete checkout'));
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
        <span className="ml-2 text-gray-600">Loading checkout details...</span>
      </div>
    );
  }

  // Show lock warning if someone else has the lock
  if (!hasLock && lockedBy) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-6">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-8 max-w-md text-center">
          <Lock className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-amber-800 mb-2">Checkout In Progress</h2>
          <p className="text-amber-700 mb-4">
            This booking is currently being processed by <strong>{lockedBy}</strong>.
          </p>
          <p className="text-sm text-amber-600 mb-6">
            Please wait for them to complete or close the tab, or try again later.
          </p>
          <Button variant="outline" onClick={() => closeTab(tabId)}>
            Close Tab
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <LogOut className="h-6 w-6 text-purple-600" />
            Checkout Review
          </h1>
          <p className="text-gray-500 mt-1">
            Review all charges before completing checkout for {guest?.name || 'Guest'}
          </p>
        </div>
        <Badge className="bg-purple-100 text-purple-700 text-sm px-3 py-1">
          Room {room?.room_number}
        </Badge>
      </div>

      <div className="flex-1 grid grid-cols-3 gap-6 min-h-0 overflow-hidden">
        {/* Left Column - Charges */}
        <ScrollArea className="col-span-2 pr-4">
          <div className="space-y-4 pb-4">
            {/* Guest & Room Info */}
            <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-blue-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Hotel className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-800">{guest?.name || 'Guest'}</h3>
                    <p className="text-sm text-gray-500">
                      Room {room?.room_number} • {room?.room_type} • {calculatedNights} Night(s)
                    </p>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-700">Checked In</Badge>
                </div>
              </CardContent>
            </Card>

            {/* Room Charges */}
            <Card>
              <CardHeader className="py-3 px-4 bg-gray-50 border-b">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Hotel className="h-4 w-4 text-gray-500" />
                  Room Charges
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-gray-800">Room {room?.room_number} - {room?.room_type}</p>
                    <p className="text-sm text-gray-500">{calculatedNights} night(s) @ ₹{(roomCharges / calculatedNights).toFixed(0)}/night</p>
                  </div>
                  <span className="text-lg font-bold text-gray-800">₹{roomCharges.toFixed(0)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Restaurant Orders */}
            {(restaurantOrders.length > 0 || manualRestaurantCharges.length > 0) && (
              <Card>
                <CardHeader className="py-3 px-4 bg-orange-50 border-b">
                  <CardTitle className="text-sm font-medium flex items-center gap-2 text-orange-700">
                    <UtensilsCrossed className="h-4 w-4" />
                    Restaurant Charges
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-2">
                  {restaurantOrders.map(order => (
                    <div key={order.id} className="flex justify-between text-sm">
                      <span className="text-gray-600">Order #{order.id?.slice(-6)}</span>
                      <span className="font-medium">₹{(order.total_amount || order.total || 0).toFixed(0)}</span>
                    </div>
                  ))}
                  {manualRestaurantCharges.map(charge => (
                    <div key={charge.id} className="flex justify-between text-sm">
                      <span className="text-gray-600">{charge.description}</span>
                      <span className="font-medium">₹{charge.amount.toFixed(0)}</span>
                    </div>
                  ))}
                  <div className="border-t pt-2 flex justify-between font-medium">
                    <span>Total Restaurant</span>
                    <span>₹{restaurantTotal.toFixed(0)}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Additional Charges */}
            <Card>
              <CardHeader className="py-3 px-4 bg-blue-50 border-b">
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-blue-700">
                  <Sparkles className="h-4 w-4" />
                  Additional Charges
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-3">
                {additionalCharges.map(charge => (
                  <div key={charge.id} className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">{charge.description}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">₹{charge.amount.toFixed(0)}</span>
                      <Button variant="ghost" size="sm" onClick={() => removeCharge(charge.id)} className="h-6 w-6 p-0 text-red-500">
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Input
                    placeholder="Description"
                    value={newCharge.description}
                    onChange={(e) => setNewCharge({ ...newCharge, description: e.target.value })}
                    className="flex-1 h-8 text-sm"
                  />
                  <Input
                    type="number"
                    placeholder="Amount"
                    value={newCharge.amount}
                    onChange={(e) => setNewCharge({ ...newCharge, amount: e.target.value })}
                    className="w-24 h-8 text-sm"
                  />
                  <Button onClick={addCharge} size="sm" variant="outline" className="h-8">
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Discount */}
            <Card>
              <CardHeader className="py-3 px-4 bg-red-50 border-b">
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-red-700">
                  <Percent className="h-4 w-4" />
                  Discount
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="flex gap-3 items-end">
                  <div className="flex-1">
                    <Label className="text-xs text-gray-500">Type</Label>
                    <Select value={discountType} onValueChange={setDiscountType}>
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="amount">Fixed Amount</SelectItem>
                        <SelectItem value="percent">Percentage</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-32">
                    <Label className="text-xs text-gray-500">{discountType === 'percent' ? 'Percentage' : 'Amount'}</Label>
                    <Input
                      type="number"
                      value={discount}
                      onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs text-gray-500">Reason</Label>
                    <Input
                      value={discountReason}
                      onChange={(e) => setDiscountReason(e.target.value)}
                      placeholder="Reason for discount"
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </ScrollArea>

        {/* Right Column - Summary & Payment */}
        <div className="flex flex-col gap-4 overflow-hidden">
          <ScrollArea className="flex-1">
            {/* Bill Summary */}
            <Card className="mb-4">
              <CardHeader className="py-3 px-4 bg-gray-50 border-b">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Receipt className="h-4 w-4" />
                  Bill Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Room Charges</span>
                  <span className="font-medium">₹{roomCharges.toFixed(0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Restaurant</span>
                  <span className="font-medium">₹{restaurantTotal.toFixed(0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Services</span>
                  <span className="font-medium">₹{serviceTotal.toFixed(0)}</span>
                </div>
                {additionalTotal > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Additional</span>
                    <span className="font-medium">₹{additionalTotal.toFixed(0)}</span>
                  </div>
                )}
                <div className="border-t pt-2 flex justify-between">
                  <span className="font-medium">Subtotal</span>
                  <span className="font-semibold">₹{subtotal.toFixed(0)}</span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex justify-between text-red-600">
                    <span>Discount</span>
                    <span>- ₹{discountAmount.toFixed(0)}</span>
                  </div>
                )}
                <div className="bg-gray-50 rounded p-2 space-y-1">
                  <p className="text-xs font-medium text-gray-500">Tax Breakdown</p>
                  {roomCharges > 0 && (
                    <div className="flex justify-between text-xs">
                      <span>Room ({taxSettings.room_tax}%)</span>
                      <span>₹{roomTax.toFixed(0)}</span>
                    </div>
                  )}
                  {restaurantTotal > 0 && (
                    <div className="flex justify-between text-xs">
                      <span>Restaurant ({taxSettings.restaurant_food_tax}%)</span>
                      <span>₹{restaurantTax.toFixed(0)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xs font-medium border-t pt-1">
                    <span>Total Tax</span>
                    <span>₹{totalTax.toFixed(0)}</span>
                  </div>
                </div>
                <div className="border-t pt-2 flex justify-between text-lg">
                  <span className="font-bold">Grand Total</span>
                  <span className="font-bold text-purple-600">₹{grandTotal.toFixed(0)}</span>
                </div>
                <div className="flex justify-between text-emerald-600">
                  <span>Advance Paid</span>
                  <span className="font-medium">- ₹{amountPaid.toFixed(0)}</span>
                </div>
                <div className="border-t pt-2 flex justify-between text-lg">
                  <span className="font-bold">Balance Due</span>
                  <span className={`font-bold ${balanceDue > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    ₹{balanceDue.toFixed(0)}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Payment Section */}
            {balanceDue > 0 && (
              <Card>
                <CardContent className="p-4">
                  <MultiPaymentInput
                    totalAmount={balanceDue}
                    payments={payments}
                    onPaymentsChange={setPayments}
                    disabled={processing}
                  />
                </CardContent>
              </Card>
            )}

            {/* Notes */}
            <Card className="mt-4">
              <CardContent className="p-4">
                <Label className="text-xs text-gray-500">Notes</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any additional notes..."
                  className="mt-1 h-20 text-sm"
                />
              </CardContent>
            </Card>
          </ScrollArea>

          {/* Action Buttons */}
          <div className="space-y-2 flex-shrink-0 pt-2 border-t">
            <Button
              onClick={handleCheckout}
              disabled={processing || (balanceDue > 0 && !isPaymentBalanced)}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white h-11"
            >
              {processing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-5 w-5 mr-2" />
                  Complete Checkout
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => closeTab(tabId)}
              className="w-full"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
